
import { computed, defineComponent, nextTick, onMounted, PropType, ref, watch } from "vue";
import { useStore } from "vuex";
import moment from "moment";
import LuxAvatar from "@/modern/components/lux/LuxAvatar.vue";
import { renderLuxMarkdown } from "@/modern/services/luxMarkdown";
import { LuxPendingMessage } from "@/modern/types/lux";

export default defineComponent({
  name: "lux-message",
  components: { LuxAvatar },
  props: {
    message: { type: Object as PropType<LuxPendingMessage>, required: true },
  },
  setup(props) {
    const store = useStore();
    const body = ref<HTMLElement | null>(null);

    const isUser = computed(() => props.message.role === "user");
    const rendered = computed(() => renderLuxMarkdown(props.message.content));
    const time = computed(() => moment(props.message.created_at).format("HH:mm"));
    const avatar = computed(() => store.getters.currentUser?.avatar ?? "/media/avatars/blank.png");

    /**
     * Coloca um botão "copiar" em cada bloco de código.
     *
     * Feito por manipulação de DOM porque o markdown entra via v-html, e
     * v-html não passa por template — não dá para pendurar @click do Vue nas
     * tags geradas. O botão lê o texto do <code>, não do <pre>, para não copiar
     * o próprio rótulo do botão junto.
     */
    function decorateCodeBlocks() {
      const container = body.value;

      if (!container) {
        return;
      }

      container.querySelectorAll("pre").forEach((pre) => {
        if (pre.querySelector(".lux-copy-btn")) {
          return;
        }

        const button = document.createElement("button");
        button.type = "button";
        button.className = "lux-copy-btn";
        button.textContent = "copiar";

        button.addEventListener("click", () => {
          const code = pre.querySelector("code");
          const text = (code ?? pre).textContent ?? "";

          const done = () => {
            button.textContent = "copiado!";
            setTimeout(() => (button.textContent = "copiar"), 1500);
          };

          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(() => (button.textContent = "erro"));
          } else {
            // Fallback para contextos sem a Clipboard API.
            const area = document.createElement("textarea");
            area.value = text;
            document.body.appendChild(area);
            area.select();
            try {
              document.execCommand("copy");
              done();
            } catch {
              button.textContent = "erro";
            }
            document.body.removeChild(area);
          }
        });

        pre.appendChild(button);
      });
    }

    onMounted(decorateCodeBlocks);
    watch(rendered, () => nextTick(decorateCodeBlocks));

    return { body, isUser, rendered, time, avatar };
  },
});
